/**
 * Admin Orca-recover RPC definition (INF-610).
 *
 * `admin.orcaTask.recover` collapses the manual failed-task recovery runbook
 * into one action: reset the `orca_tasks` row, flip the Linear issue back to a
 * pickable state (with read-back verification), refresh the issue's cache row,
 * and kick the owning project's sweep. The RPC returns a per-step report so
 * partial failures are visible rather than swallowed. Gated by AdminMiddleware.
 *
 * Kept in its own group (mirroring `admin.linearCache.backfill`) so the extra
 * LinearClient + OrcaProjectScheduler dependencies stay scoped to this handler
 * rather than widening the `admin.orcaTask.*` CRUD group's footprint.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";
import { AdminMiddleware } from "../admin.tags.ts";
import { Forbidden, Unauthorized, ValidationError } from "../errors.ts";

// ============================================================================
// Request / Response
// ============================================================================

/**
 * Input: the Linear issue identifier to recover (e.g. "INF-999").
 *
 * @since 1.0.0
 */
export const OrcaTaskRecoverInput = Schema.Struct({
  issueIdentifier: Schema.String,
});
export type OrcaTaskRecoverInput = Schema.Schema.Type<typeof OrcaTaskRecoverInput>;

/**
 * One recovery step's outcome. `ok: false` flags a step the operator must
 * inspect; `detail` carries the human-readable result either way.
 *
 * @since 1.0.0
 */
export const OrcaRecoverStep = Schema.Struct({
  name: Schema.String,
  ok: Schema.Boolean,
  detail: Schema.String,
});
export type OrcaRecoverStep = Schema.Schema.Type<typeof OrcaRecoverStep>;

/**
 * Result payload: the recovered identifier plus the ordered step report.
 *
 * @since 1.0.0
 */
export const OrcaTaskRecoverResult = Schema.Struct({
  issueIdentifier: Schema.String,
  steps: Schema.Array(OrcaRecoverStep),
});
export type OrcaTaskRecoverResult = Schema.Schema.Type<typeof OrcaTaskRecoverResult>;

// ============================================================================
// RPC Group
// ============================================================================

/**
 * Admin Orca-recover RPC group. Gated by AdminMiddleware.
 *
 * @since 1.0.0
 */
export class AdminOrcaRecoverRpcs extends RpcGroup.make(
  Rpc.make("admin.orcaTask.recover", {
    payload: OrcaTaskRecoverInput,
    success: OrcaTaskRecoverResult,
    error: Schema.Union(Unauthorized, Forbidden, ValidationError),
  }).middleware(AdminMiddleware),
) {}
